// Personal SAP account (PM-USER-ATTRIBUTION.md Layer C) — self-service: a user who owns a
// personal SAP logon saves it here, and every PM WRITE then runs in SAP under their own name
// (real user in SAP change documents). Without one, writes use the shared technical user and
// the portal's actor field carries the attribution. The password is verified against SAP
// before it is stored (a typo would otherwise lock the SAP user after a few writes) and is
// never sent back to the client.
import React, { useEffect, useState } from 'react';
import { KeyRound, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { Card, Button } from './ui';
import { getSapAccount, saveSapAccount, deleteSapAccount } from '../api';

export default function SapAccountCard({ user }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null); // null=loading, {configured, sapUsername?}
  const [sapUsername, setSapUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}
  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  useEffect(() => {
    let alive = true;
    getSapAccount(user)
      .then((res) => { if (alive) setInfo(res); })
      .catch(() => { if (alive) setInfo({ configured: false }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onSave = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await saveSapAccount(user, sapUsername.trim(), password);
      if (res.ok === false) {
        setMsg({ ok: false, text: res.error });
      } else {
        setInfo({ configured: true, sapUsername: res.sapUsername });
        setPassword('');
        setMsg({ ok: true, text: t('pm.sapacct.saved') });
      }
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    }
    setBusy(false);
  };

  const onRemove = async () => {
    setBusy(true); setMsg(null);
    try {
      await deleteSapAccount(user);
      setInfo({ configured: false });
      setSapUsername('');
      setMsg({ ok: true, text: t('pm.sapacct.removed') });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    }
    setBusy(false);
  };

  const inputCls = `w-full rounded-xl px-3 py-2 text-sm ${isDarkMode
    ? 'bg-white/10 text-white placeholder-white/40'
    : 'bg-slate-100 text-slate-800 placeholder-slate-400'}`;

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${sub}`}
      >
        <KeyRound size={14} />
        {t('pm.sapacct.title')}
        {info && info.configured && (
          <span className={isDarkMode ? 'text-emerald-300 normal-case' : 'text-emerald-600 normal-case'}>
            · {info.sapUsername}
          </span>
        )}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <Card className="mt-2">
          <p className={`text-sm mb-3 ${sub}`}>
            {info && info.configured
              ? t('pm.sapacct.statusOwn', { user: info.sapUsername })
              : t('pm.sapacct.statusShared')}
          </p>
          <div className="space-y-2">
            <input
              value={sapUsername}
              onChange={(e) => setSapUsername(e.target.value.toUpperCase())}
              placeholder={t('pm.sapacct.username')}
              maxLength={12}
              autoComplete="off"
              className={inputCls}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('pm.sapacct.password')}
              autoComplete="new-password"
              className={inputCls}
            />
            <div className="flex gap-2">
              <Button onClick={onSave} disabled={busy || !sapUsername.trim() || !password}>
                {busy ? t('pm.common.loading') : t('pm.sapacct.save')}
              </Button>
              {info && info.configured && (
                <Button variant="secondary" onClick={onRemove} disabled={busy}>
                  {t('pm.sapacct.remove')}
                </Button>
              )}
            </div>
          </div>
          {msg && (
            <p className={`text-sm mt-2 ${msg.ok
              ? (isDarkMode ? 'text-emerald-300' : 'text-emerald-600')
              : (isDarkMode ? 'text-rose-300' : 'text-rose-600')}`}>
              {msg.text}
            </p>
          )}
          <p className={`text-xs mt-3 ${sub}`}>{t('pm.sapacct.hint')}</p>
        </Card>
      )}
    </section>
  );
}
